import type { ErrorEvent } from "@sentry/react";

/**
 * Errores conocidos que no son bugs accionables y que conviene tirar
 * antes de mandarlos a Sentry. Mantener corto y específico — cada entrada
 * acá es un evento que dejás de ver, así que la justificación importa.
 */
const IGNORED_ERRORS: (string | RegExp)[] = [
  // Bug famoso de Chrome/Edge: el observer dispara este "error" cuando hace
  // más de un layout pass en el mismo frame. Inocuo, ruido puro.
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,

  // Usuario canceló la navegación / un fetch abortado. No es un bug.
  /AbortError/i,
  /The operation was aborted/i,
  /Request aborted/i,
  /canceled/i,

  // "Script error." sin más info viene de scripts cross-origin sin CORS.
  // No tenemos stack ni mensaje útil, no sirve para nada.
  "Script error.",

  // Promise rejection vacía (algún código hace `Promise.reject()` sin valor).
  // Sin contenido no podemos accionarla.
  "Non-Error promise rejection captured with value: undefined",
  "Non-Error promise rejection captured with value: null",
];

/**
 * URLs cuyos errores ignoramos. La mayoría son extensiones del browser
 * que inyectan código y rompen — no son bugs de Trud.
 */
const DENY_URLS: RegExp[] = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /^safari-web-extension:\/\//i,
];

/**
 * Keys cuyos valores son sensibles y nunca deberían viajar a Sentry.
 * El matcheo es case-insensitive y por substring, así que `passwordConfirm`,
 * `currentPassword`, `accessToken`, `refresh_token`, etc. quedan cubiertos.
 */
const SENSITIVE_KEY_PATTERN =
  /password|token|secret|api[_-]?key|cvv|card[_-]?number|authorization|cookie/i;

const REDACTED = "[REDACTED]";
const MAX_DEPTH = 8;

function scrubDeep(value: unknown, depth: number): unknown {
  if (depth > MAX_DEPTH || value === null || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => scrubDeep(item, depth + 1));
  }

  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    result[key] = SENSITIVE_KEY_PATTERN.test(key)
      ? REDACTED
      : scrubDeep(val, depth + 1);
  }
  return result;
}

export const IGNORE_ERRORS = IGNORED_ERRORS;
export const DENY_URL_LIST = DENY_URLS;

/**
 * beforeSend hook. Camina `extra` y `contexts` masking keys sensibles.
 * Si en el futuro hay que filtrar por endpoint, ese es el lugar para
 * agregarlo.
 */
export function beforeSendScrub(event: ErrorEvent): ErrorEvent {
  if (event.extra) {
    event.extra = scrubDeep(event.extra, 0) as Record<string, unknown>;
  }
  if (event.contexts) {
    event.contexts = scrubDeep(event.contexts, 0) as ErrorEvent["contexts"];
  }
  if (event.request?.data) {
    event.request.data = scrubDeep(event.request.data, 0);
  }
  return event;
}
